import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #042E4B 0%, #0A4A6F 100%);
  padding: 3rem;
`;

const Title = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 700;
  color: white;
  margin-bottom: 3rem;
  text-align: center;
`;

const FlowContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  max-width: 1100px;
  width: 100%;
`;

const FlowRow = styled.div<{ hasThreeColumns?: boolean }>`
  display: grid;
  grid-template-columns: ${props => props.hasThreeColumns ? '1fr 1fr 1fr' : '1fr'};
  gap: 2rem;
  width: 100%;
  align-items: center;
  position: relative;
`;

const SideColumnsRow = styled.div`
  display: grid;
  grid-template-columns: 300px 400px 300px;
  gap: 2rem;
  width: 100%;
  align-items: start;
  position: relative;
  justify-content: center;
`;

const ModuleCard = styled(motion.div)<{ isCenter?: boolean; label?: string }>`
  background: ${props => props.isCenter 
    ? 'linear-gradient(135deg, #1a3a52 0%, #042E4B 100%)' 
    : 'linear-gradient(135deg, #607C90 0%, #4a5f70 100%)'};
  color: white;
  padding: 1.8rem 2.5rem;
  border-radius: 12px;
  font-size: ${props => props.isCenter ? '2rem' : '1.6rem'};
  font-weight: 700;
  text-align: center;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  position: relative;
  min-height: ${props => props.isCenter ? '80px' : '70px'};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  }
`;

const SideLabel = styled.div`
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  font-style: italic;
  white-space: nowrap;
`;

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
`;

const Arrow = styled(motion.div)`
  width: 0;
  height: 0;
  border-left: 20px solid transparent;
  border-right: 20px solid transparent;
  border-top: 30px solid #EF443C;
  margin: -5px 0;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3));
`;

const HorizontalArrowContainer = styled.div`
  position: absolute;
  top: 50%;
  display: flex;
  align-items: center;
  z-index: 5;
`;

const HorizontalArrow = styled(motion.div)<{ direction: 'left' | 'right' }>`
  width: 0;
  height: 0;
  border-top: 15px solid transparent;
  border-bottom: 15px solid transparent;
  ${props => props.direction === 'right' 
    ? 'border-left: 25px solid #EF443C;' 
    : 'border-right: 25px solid #EF443C;'
  }
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
`;

const LoopArrow = styled(motion.div)`
  position: absolute;
  right: -80px;
  top: 50%;
  width: 60px;
  height: 200px;
  border: 4px solid #EF443C;
  border-left: none;
  border-radius: 0 30px 30px 0;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3));
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    right: -4px;
    width: 0;
    height: 0;
    border-left: 15px solid transparent;
    border-right: 15px solid transparent;
    border-top: 25px solid #EF443C;
  }
`;

const Slide9Metodologia: React.FC = () => {
  return (
    <Container>
      <Title
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        Metodologia Completa
      </Title>

      <FlowContainer>
        {/* Missão */}
        <ModuleCard
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.25, delay: 0.05 }}
        >
          Missão
        </ModuleCard>
        <Arrow
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.15, delay: 0.15 }}
        />

        {/* Visão */}
        <ModuleCard
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.25, delay: 0.2 }}
        >
          Visão
        </ModuleCard>
        <Arrow
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.15, delay: 0.3 }}
        />

        {/* Valores */}
        <ModuleCard
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.25, delay: 0.35 }}
        >
          Valores
        </ModuleCard>
        <Arrow
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.15, delay: 0.45 }}
        />

        {/* Linha com Ambiente Interno, MEU NEGÓCIO e Ambiente Externo */}
        <SideColumnsRow>
          <SideColumn style={{ position: 'relative', marginTop: '30px' }}>
            <SideLabel>Ambiente Interno</SideLabel>
            <ModuleCard
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.25, delay: 0.5 }}
            >
              Pontos Fortes
            </ModuleCard>
            <ModuleCard
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.25, delay: 0.55 }}
            >
              Pontos Fracos
            </ModuleCard>
            <HorizontalArrowContainer style={{ left: '100%', marginLeft: '10px' }}>
              <HorizontalArrow
                direction="right"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.15, delay: 0.65 }}
              />
            </HorizontalArrowContainer>
          </SideColumn>

          <ModuleCard
            isCenter
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.25, delay: 0.6 }}
            style={{ marginTop: '30px' }}
          >
            MEU NEGÓCIO
          </ModuleCard>

          <SideColumn style={{ position: 'relative', marginTop: '30px' }}>
            <SideLabel>Ambiente Externo</SideLabel>
            <ModuleCard
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.25, delay: 0.7 }}
            >
              Oportunidades
            </ModuleCard>
            <ModuleCard
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.25, delay: 0.75 }}
            >
              Ameaças
            </ModuleCard>
            <HorizontalArrowContainer style={{ right: '100%', marginRight: '10px' }}>
              <HorizontalArrow
                direction="left"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.15, delay: 0.85 }}
              />
            </HorizontalArrowContainer>
          </SideColumn>
        </SideColumnsRow>

        <Arrow
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.15, delay: 0.9 }}
        />

        {/* Cruzamento SWOT */}
        <ModuleCard
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.25, delay: 0.95 }}
        >
          Cruzamento SWOT
        </ModuleCard>
        <Arrow
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.15, delay: 1.05 }}
        />

        {/* Metas Globais */}
        <ModuleCard
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.25, delay: 1.1 }}
        >
          Metas Globais
        </ModuleCard>
        <Arrow
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.15, delay: 1.2 }}
        />

        {/* Planos de Ação */}
        <ModuleCard
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.25, delay: 1.25 }}
          style={{ position: 'relative' }}
        >
          Planos de Ação
          <LoopArrow
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25, delay: 1.6 }}
          />
        </ModuleCard>
        <Arrow
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.15, delay: 1.35 }}
        />

        {/* Monitoramento */}
        <ModuleCard
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.25, delay: 1.4 }}
        >
          Monitoramento
        </ModuleCard>
        <Arrow
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.15, delay: 1.5 }}
        />

        {/* Avaliação */}
        <ModuleCard
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.25, delay: 1.55 }}
        >
          Avaliação
        </ModuleCard>
      </FlowContainer>
    </Container>
  );
};

export default Slide9Metodologia;
